"use client";

/**
 * Brand Switch Context
 * 
 * Manages the currently active brand across the app.
 * When a user switches brands, this context updates and all
 * components that use useBrand() will re-render with the new brand data.
 * 
 * The selected brand is:
 * 1. Persisted to the server via POST /api/user/brands
 * 2. Cached in localStorage for quick access on page reload
 * 3. Synced with the auth context for consistent state
 */

import {
	createContext,
	useContext,
	useState,
	useEffect,
	useCallback,
	useMemo,
	type ReactNode,
} from "react";
import useSWR, { mutate as globalMutate } from "swr";
import { useIsAuthenticated } from "@/hooks/use-auth";
import { isDevPlaceholderBrandId } from "@/lib/brand-resolution";

// Types
export interface BrandInfo {
	id: string;
	name: string;
	slug: string | null;
	handle: string | null;
	colorPrimary: string | null;
	logoUrl: string | null;
	brandImage: string | null;
	orgId: string;
	orgName: string;
	role: string;
	isActive: boolean;
	connectedPlatforms?: string[];
	timezone?: string | null;
}

interface BrandSwitchContextValue {
	/** All brands the user has access to */
	brands: BrandInfo[];
	/** Currently active brand */
	activeBrand: BrandInfo | null;
	/** ID of the currently active brand */
	activeBrandId: string | null;
	/** Whether brands are loading */
	isLoading: boolean;
	/** Whether a brand switch is in progress */
	isSwitching: boolean;
	/** Error if any */
	error: Error | null;
	/** Switch to a different brand (pass null to clear / select All Brands) */
	switchBrand: (brandId: string | null) => Promise<boolean>;
	/** Refresh the brands list */
	refreshBrands: () => Promise<void>;
}

// Local storage key for caching active brand
const ACTIVE_BRAND_KEY = "vastipost:active_brand_id";

// Sentinel value to indicate explicit account-level / all-brands view
const ACCOUNT_VIEW = "__account__";

// SWR fetcher
const fetcher = async (url: string) => {
	const res = await fetch(url);
	if (!res.ok) {
		const payload = await res.json().catch(() => null) as { error?: string } | null;
		if (res.status === 401) {
			throw new Error("Unauthorized");
		}

		throw new Error(payload?.error ?? "Failed to fetch brands");
	}
	return res.json();
};

// Context
const BrandSwitchContext = createContext<BrandSwitchContextValue | null>(null);

/**
 * Provider Props
 */
interface BrandSwitchProviderProps {
	children: ReactNode;
	/** Optional initial brand ID (from server) */
	initialBrandId?: string | null;
}

/**
 * Brand Switch Provider
 * 
 * Wrap your app with this to enable brand switching across all pages.
 * 
 * @example
 * ```tsx
 * // In layout.tsx
 * <BrandSwitchProvider>
 *   {children}
 * </BrandSwitchProvider>
 * ```
 */
export function BrandSwitchProvider({
	children,
	initialBrandId,
}: BrandSwitchProviderProps) {
	const isAuthenticated = useIsAuthenticated();

	// Track switching state
	const [isSwitching, setIsSwitching] = useState(false);
	
	// Track active brand ID separately for immediate UI updates
	const [localBrandId, setLocalBrandId] = useState<string | null>(() => {
		// Try to get from localStorage on initial load
		if (typeof window !== "undefined") {
			const cached = localStorage.getItem(ACTIVE_BRAND_KEY);
			if (cached) return cached;
		}
		return initialBrandId ?? null;
	});

	// Fetch brands from API
	const {
		data,
		error,
		isLoading,
		mutate: refreshBrands,
	} = useSWR<{ brands: BrandInfo[]; activeBrandId: string | null }>(
		isAuthenticated ? "/api/user/brands" : null,
		fetcher,
		{
			revalidateOnFocus: false,
			dedupingInterval: 60 * 1000, // 1 minute
		}
	);

	const brands = data?.brands ?? [];
	const serverActiveBrandId = data?.activeBrandId ?? null;

	// Keep the local active brand selection aligned with the server-owned brand context.
	useEffect(() => {
		if (typeof window === "undefined" || isSwitching) {
			return;
		}

		const nextServerBrandId = serverActiveBrandId ?? ACCOUNT_VIEW;
		const hasLocalBrand = typeof localBrandId === "string"
			&& localBrandId !== ACCOUNT_VIEW
			&& brands.some((brand) => brand.id === localBrandId);
		const shouldSyncToServer = !localBrandId
			|| isDevPlaceholderBrandId(localBrandId)
			|| (!hasLocalBrand && localBrandId !== ACCOUNT_VIEW)
			|| localBrandId !== nextServerBrandId;

		if (!shouldSyncToServer) {
			return;
		}

		setLocalBrandId(nextServerBrandId);
		localStorage.setItem(ACTIVE_BRAND_KEY, nextServerBrandId);
	}, [brands, isSwitching, localBrandId, serverActiveBrandId]);

	// Get the active brand object
	const activeBrand = useMemo(() => {
		// Account view: user explicitly chose "All Brands"
		if (localBrandId === ACCOUNT_VIEW) {
			return null;
		}

		if (brands.length === 0) {
			return null;
		}

		if (localBrandId) {
			const locallySelectedBrand = brands.find((brand) => brand.id === localBrandId);
			if (locallySelectedBrand) {
				return locallySelectedBrand;
			}
		}

		if (serverActiveBrandId) {
			const serverSelectedBrand = brands.find((brand) => brand.id === serverActiveBrandId);
			if (serverSelectedBrand) {
				return serverSelectedBrand;
			}
		}

		// Fall back to the first accessible brand only when the server has no explicit selection.
		return brands[0] ?? null;
	}, [brands, localBrandId, serverActiveBrandId]);

	// The actual active brand ID (might differ from localBrandId if brand doesn't exist)
	const activeBrandId = localBrandId === ACCOUNT_VIEW ? null : (activeBrand?.id ?? null);

	/**
	 * Switch to a different brand
	 */
	const switchBrand = useCallback(
		async (brandId: string | null): Promise<boolean> => {
			// null means "All Brands" (account-level view)
			if (brandId === null) {
				setLocalBrandId(ACCOUNT_VIEW);
				localStorage.setItem(ACTIVE_BRAND_KEY, ACCOUNT_VIEW);
				setIsSwitching(true);
				try {
					const response = await fetch("/api/user/brands", {
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({ brandId: null }),
					});
					if (!response.ok) throw new Error("Failed to clear brand");
					console.log("[BrandSwitch] Cleared active brand (All Brands view)");
					await Promise.all([
						globalMutate("/api/user/brands"),
						globalMutate("/api/user/profile"),
						globalMutate((key) =>
							typeof key === "string" && key.includes("brandId="),
							undefined,
							{ revalidate: true }
						),
					]);
					return true;
				} catch (err) {
					console.error("[BrandSwitch] Error clearing brand:", err);
					return false;
				} finally {
					setIsSwitching(false);
				}
			}

			// Validate brand exists
			const targetBrand = brands.find((b) => b.id === brandId);
			if (!targetBrand) {
				console.error("[BrandSwitch] Brand not found:", brandId);
				return false;
			}

			// Optimistic update
			setLocalBrandId(brandId);
			localStorage.setItem(ACTIVE_BRAND_KEY, brandId);
			setIsSwitching(true);

			try {
				// Persist to server
				const response = await fetch("/api/user/brands", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({ brandId }),
				});

				if (!response.ok) {
					throw new Error("Failed to switch brand");
				}

				console.log("[BrandSwitch] Successfully switched to:", targetBrand.name);

				// Invalidate any brand-specific caches
				// This will cause pages using brand data to refetch
				await Promise.all([
					globalMutate("/api/user/brands"),
					globalMutate("/api/user/profile"),
					globalMutate((key) => 
						typeof key === "string" && key.includes("brandId="),
						undefined,
						{ revalidate: true }
					),
				]);

				return true;
			} catch (err) {
				console.error("[BrandSwitch] Error switching brand:", err);
				// Rollback on error
				setLocalBrandId(activeBrand?.id ?? null);
				if (activeBrand?.id) {
					localStorage.setItem(ACTIVE_BRAND_KEY, activeBrand.id);
				}
				return false;
			} finally {
				setIsSwitching(false);
			}
		},
		[brands, activeBrand]
	);

	/**
	 * Refresh brands list
	 */
	const handleRefreshBrands = useCallback(async () => {
		await refreshBrands();
	}, [refreshBrands]);

	// Context value
	const value = useMemo<BrandSwitchContextValue>(
		() => ({
			brands,
			activeBrand,
			activeBrandId,
			isLoading,
			isSwitching,
			error: error ?? null,
			switchBrand,
			refreshBrands: handleRefreshBrands,
		}),
		[
			brands,
			activeBrand,
			activeBrandId,
			isLoading,
			isSwitching,
			error,
			switchBrand,
			handleRefreshBrands,
		]
	);

	return (
		<BrandSwitchContext.Provider value={value}>
			{children}
		</BrandSwitchContext.Provider>
	);
}

/**
 * Hook to access brand switching functionality
 * 
 * @example
 * ```tsx
 * const { activeBrand, switchBrand, brands } = useBrandSwitch();
 * 
 * // Switch to a different brand
 * await switchBrand("new-brand-id");
 * ```
 */
export function useBrandSwitch(): BrandSwitchContextValue {
	const context = useContext(BrandSwitchContext);
	if (!context) {
		throw new Error("useBrandSwitch must be used within a BrandSwitchProvider");
	}
	return context;
}

/**
 * Hook to get just the active brand (convenience wrapper)
 * Use this when you only need the current brand, not switching functionality
 * 
 * @example
 * ```tsx
 * const brand = useActiveBrand();
 * console.log(brand?.name); // "EverTale Stories"
 * ```
 */
export function useActiveBrand(): BrandInfo | null {
	const context = useContext(BrandSwitchContext);
	// If not in provider, return null gracefully (for backwards compatibility)
	return context?.activeBrand ?? null;
}

/**
 * Hook to get just the active brand ID (convenience wrapper)
 * Use this for API calls that need the brand ID
 * 
 * @example
 * ```tsx
 * const brandId = useActiveBrandId();
 * const { data } = useSWR(`/api/posts?brandId=${brandId}`);
 * ```
 */
export function useActiveBrandId(): string | null {
	const context = useContext(BrandSwitchContext);
	return context?.activeBrandId ?? null;
}
